import { Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from '../shell/AppShell'
import { ModuleGate } from '../shell/ModuleGate'
import { RelayRoutes } from './RelayRoutes'
import { takeReturnTo } from '../shell/returnTo'
import { DocsSignin } from '../shell/DocsSignin'
import { DOCS_SIGNIN } from '../shell/docsLinks'

/**
 * The standalone portal: this shell's chrome, with modules mounted inside it.
 *
 * A copy of the AboveStore shell's shape rather than a dependency on it, because Relay ships to
 * customers who do not run that platform. The two can still interoperate — this module is also
 * exposed as a federation remote — but neither product requires the other to be installed.
 *
 * Federated remotes (Reporting, Dashboards) mount here too, each only when its URL is configured;
 * see src/shell/modules.ts. Those routes land with the remotes themselves.
 */

/**
 * Where the identity provider returns to. Rendering a redirect rather than a page keeps the OIDC
 * response parameters out of history — `main.tsx` has already stripped them by the time this runs.
 */
function CallbackLanding() {
  // Where the person was going when sign-in started, or the dashboard.
  return <Navigate to={takeReturnTo()} replace />
}

function RelayPage() {
  return (
    <ModuleGate moduleName="relay" permissionKey="device.view">
      <RelayRoutes />
    </ModuleGate>
  )
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<AppShell />}>
        {/*
          Relay mounts at the ROOT, not at /relay.

          Its routes are written as the Blazor client's own URLs — /devices, /admin/users,
          /ota/rollouts — so mounting them under a prefix left every one of them unmatched, and the
          module's catch-all answered instead. The symptom was the Phase 0 scaffold rendering for
          every page in the application, which is what shipped until it was opened in a browser.

          Both an index route and a splat, because a splat alone does not answer the empty
          remainder: without the index, "/" would not reach the module at all.
        */}
        <Route index element={<RelayPage />} />
        <Route path="*" element={<RelayPage />} />
      </Route>
      {/*
        Keycloak returns here after sign-in. It must be matched BEFORE the module's splat, or the
        module answers it and the callback renders a "not found" instead of landing the session.
      */}
      <Route path="/callback" element={<CallbackLanding />} />
      {/*
        The user documentation's sign-in (Plans/user-documentation.md): outside the shell and the module's
        device.view gate, because anyone who can sign in may read the docs.
      */}
      <Route path={`${DOCS_SIGNIN}/*`} element={<DocsSignin />} />
      {/*
        Accepting an invitation, outside the shell: until it is accepted the relay doesn't know who the
        signed-in person is, and the shell's profile would fail.
      */}
    </Routes>
  )
}

export default App
